import React, { useCallback, useState } from "react";

import { ProductCategoryFilterValue } from "components/product-category-filter";
import { ProductCatalogOption } from "services/types/products.type";

type ProductCatalogFieldProviderProps = {
  children?: React.ReactNode;
};

export type ProductSelectedInfo = {
  selectedQuantity: number;
  isSelectedByInventoryId: boolean;
};

const ProductCatalogFieldContext = React.createContext<{
  getIsProductSelectedInfo: (
    product: ProductCatalogOption,
    name: string
  ) => ProductSelectedInfo;
  rememberSelectedProduct: (
    product: ProductCatalogOption,
    name: string
  ) => void;
  forgetSelectedProduct: (name: string) => void;
  categoryFilters: Record<string, CategoryFilter>;
  setCategoryFilter: (key: string, value: CategoryFilter) => void;
} | null>(null);

export type CategoryFilter = ProductCategoryFilterValue;

type SelectedProductDetails = {
  field: string;
  fromInventory: boolean;
  inventoryId?: number;
};

type SelectedProductsState = {
  selectedByField: Record<string, number>;
  selectedProductDetails: Record<number, SelectedProductDetails[]>;
};

export function ProductCatalogFieldProvider({
  children
}: ProductCatalogFieldProviderProps) {
  const [selectedProducts, setSelectedProducts] =
    useState<SelectedProductsState>({
      selectedByField: {},
      selectedProductDetails: {}
    });

  // Keyed by product type, so the right ear opens on the category the left
  // ear chose while a receiver picker in the same flow keeps its own.
  const [categoryFilters, setCategoryFilters] = useState<
    Record<string, CategoryFilter>
  >({});
  const setCategoryFilter = useCallback(
    (key: string, value: CategoryFilter) =>
      setCategoryFilters((current) => ({ ...current, [key]: value })),
    []
  );

  const getIsProductSelectedInfo = useCallback(
    (product: ProductCatalogOption, name: string) => {
      const { inventory_id, id } = product;

      const { selectedProductDetails } = selectedProducts;
      const selected = (selectedProductDetails[id] || []).filter(
        ({ field }) => field !== name
      );

      return {
        selectedQuantity: selected.length,
        isSelectedByInventoryId:
          Boolean(inventory_id) &&
          selected.some(
            ({ fromInventory, inventoryId }) =>
              fromInventory && inventoryId === inventory_id
          )
      } as ProductSelectedInfo;
    },
    [selectedProducts]
  );

  const rememberSelectedProduct = useCallback(
    (product: ProductCatalogOption, name: string) => {
      const { inventory_id, id } = product;
      const { selectedByField, selectedProductDetails } = structuredClone(
        selectedProducts
      ) as SelectedProductsState;

      if (selectedByField[name] && selectedByField[name] !== id) {
        selectedProductDetails[selectedByField[name]] = selectedProductDetails[
          selectedByField[name]
        ].filter(({ field }) => field !== name);
      }
      selectedByField[name] = id;

      if (!selectedProductDetails[id]) {
        selectedProductDetails[id] = [];
      }

      selectedProductDetails[id].push({
        field: name,
        fromInventory: Boolean(inventory_id),
        inventoryId: inventory_id
      });

      setSelectedProducts({
        selectedByField,
        selectedProductDetails
      });
    },
    [selectedProducts]
  );

  const forgetSelectedProduct = useCallback(
    (name: string) => {
      const { selectedByField, selectedProductDetails } = structuredClone(
        selectedProducts
      ) as SelectedProductsState;
      const productId = selectedByField[name];
      if (!productId) return;
      selectedProductDetails[productId] = selectedProductDetails[
        productId
      ].filter(({ field }) => field !== name);
      delete selectedByField[name];

      setSelectedProducts({
        selectedByField,
        selectedProductDetails
      });
    },
    [selectedProducts]
  );

  return (
    <ProductCatalogFieldContext.Provider
      value={{
        getIsProductSelectedInfo,
        rememberSelectedProduct,
        forgetSelectedProduct,
        categoryFilters,
        setCategoryFilter
      }}
    >
      {children}
    </ProductCatalogFieldContext.Provider>
  );
}

export default function useProductCatalogField() {
  const context = React.useContext(ProductCatalogFieldContext);

  if (!context) return {};

  const {
    getIsProductSelectedInfo,
    rememberSelectedProduct,
    forgetSelectedProduct
  } = context;

  return {
    getIsProductSelectedInfo,
    rememberSelectedProduct,
    forgetSelectedProduct
  };
}

// The category a ProductCatalogField filters by. Shared across the flow when a
// provider wraps it, local to the field otherwise.
export function useCategoryFilter(key: string) {
  const context = React.useContext(ProductCatalogFieldContext);
  const [localFilter, setLocalFilter] = useState<CategoryFilter>("");

  if (!context) {
    return [localFilter, setLocalFilter] as const;
  }
  return [
    context.categoryFilters[key] ?? "",
    (value: CategoryFilter) => context.setCategoryFilter(key, value)
  ] as const;
}
